import { useState } from "react";
import "./App.css";
import logo from "./bevonkte-logo.png";

/* ================= CONSTANTS ================= */
const RATE = 3.8;
const SUN_HOURS = 6;
const PV_LOSS_FACTOR = 1.25;

/* ================= INVERTERS ================= */
const SINGLE_PHASE = [5, 6, 8, 12, 16];
const THREE_PHASE = [12, 15, 20, 30, 50];

/* ================= APPLIANCES ================= */
const APPLIANCES = [
  { name: "Lights", kw: 0.5, auto: true },
  { name: "Fridge", kw: 0.3 },
  { name: "Deep Freezer", kw: 0.3 },
  { name: "TV", kw: 0.15 },

  { name: "Geyser 150L", kw: 3.0 },
  { name: "Geyser 200L", kw: 4.0 },

  { name: "Kettle", kw: 2.0 },
  { name: "Microwave", kw: 1.2 },
  { name: "Oven", kw: 3.0 },

  { name: "Washing Machine", kw: 1.0 },
  { name: "Dishwasher", kw: 1.8 },

  { name: "Pool Pump", kw: 1.1 },
  { name: "Borehole Pump", kw: 1.5 },

  { name: "Aircon 12k BTU", kw: 1.2 },
  { name: "Aircon 18k BTU", kw: 1.8 },
  { name: "Aircon 24k BTU", kw: 2.4 }
];

export default function App() {

  /* ================= CLIENT ================= */
  const [client, setClient] = useState({
    name: "", surname: "", phone: "", email: "", address: ""
  });

  /* ================= USAGE ================= */
  const [usageType, setUsageType] = useState("rand");
  const [monthlyRand, setMonthlyRand] = useState(0);
  const [monthlyKwhInput, setMonthlyKwhInput] = useState(0);

  /* ================= BATTERY % ================= */
  const [offGridPercent, setOffGridPercent] = useState(1);

  /* ================= PHASE ================= */
  const [phase, setPhase] = useState("single");

  /* ================= WHATSAPP ================= */
  const [whatsAppSent, setWhatsAppSent] = useState(false);

  /* ================= APPLIANCES ================= */
  const [qty, setQty] = useState(
    Object.fromEntries(APPLIANCES.map(a => [a.name, 0]))
  );
/* ================ ROOF INFORMATION ================ */
const [roofType, setRoofType] = useState("");
const [roofLevel, setRoofLevel] = useState("");

  /* ================= ENERGY ================= */
  const monthlyKwh =
    usageType === "rand"
      ? monthlyRand / RATE
      : monthlyKwhInput;

  const dailyKwh = monthlyKwh / 30;

  /* ================= LOAD ================= */
  let connectedLoadKw = 0;

  APPLIANCES.forEach(a => {
    const q = qty[a.name];
    if (a.auto && q > 0) connectedLoadKw += a.kw;
    if (!a.auto) connectedLoadKw += q * a.kw;
  });

  /* ================= BATTERY ================= */
  const HOURS = 12;
  const DIVERSITY_DIVISOR = 3;

  const realisticLoadKw = connectedLoadKw / DIVERSITY_DIVISOR;
  const batteryKwh = realisticLoadKw * HOURS * offGridPercent;

  /* ================= INVERTER ================= */
  const inverterList =
    phase === "single" ? SINGLE_PHASE : THREE_PHASE;

  const perPhaseLoad =
    phase === "three"
      ? realisticLoadKw / 3
      : realisticLoadKw;

  const inverterKw =
    inverterList.find(i => i >= perPhaseLoad) ||
    inverterList[inverterList.length - 1];

  /* ================= PV ================= */
  const pvEnergyKw =
    (dailyKwh / SUN_HOURS) * PV_LOSS_FACTOR;

  const pvTotalKw =
    pvEnergyKw + inverterKw;

  /* ================= WHATSAPP HANDLER ================= */
  const sendWhatsApp = () => {

    const applianceList = APPLIANCES
      .filter(a => qty[a.name] > 0)
      .map(a => `${a.name}: ${qty[a.name]} x (${a.kw} kW)`)
      .join("\n");

    const message = `
BEVONKTE SOLAR ENQUIRY

CLIENT DETAILS
Name: ${client.name} ${client.surname}
Phone: ${client.phone}
Email: ${client.email}
Address: ${client.address}

CLIENT SELECTIONS
Usage type: ${usageType}
Monthly Rand: R${monthlyRand}
Monthly kWh: ${monthlyKwhInput}
Battery %: ${offGridPercent * 100}%
Phase: ${phase === "single" ? "Three Phase" : "Single Phase"}

APPLIANCES SELECTED
${applianceList || "None"}

SYSTEM RESULT
Connected load: ${connectedLoadKw.toFixed(1)} kW
Realistic load: ${realisticLoadKw.toFixed(1)} kW
Battery required: ${batteryKwh.toFixed(1)} kWh
Inverter size: ${inverterKw} kW
PV total: ${pvTotalKw.toFixed(1)} kWp
`;

    const url =
      "https://wa.me/27677168285?text=" +
      encodeURIComponent(message);

    window.open(url, "_blank");
    setWhatsAppSent(true);
  };

  return (
    <div className="app-container">

      {/* HEADER */}
      <div className="header">
        <img src={logo} alt="Bevonkte Projects" className="logo" />
        <h1>Bevonkte Projects – Solar Consulting</h1>
      </div>

      {/* CLIENT */}
      <div className="card">
        <h2>Client Details</h2>
        {Object.keys(client).map(k => (
          <input
            key={k}
            placeholder={k}
            onChange={e =>
              setClient({ ...client, [k]: e.target.value })
            }
          />
        ))}
      </div>

      {/* USAGE */}
      <div className="card">
        <h2>Monthly Electricity</h2>

        <div className="button-row">
          <button
            className={`btn btn-purple ${usageType==="rand" && "btn-neon"}`}
            onClick={() => setUsageType("rand")}
          >
            Rand (R)
          </button>
          <button
            className={`btn btn-purple ${usageType==="kwh" && "btn-neon"}`}
            onClick={() => setUsageType("kwh")}
          >
            kWh
          </button>
        </div>

        {usageType === "rand" ? (
          <input
            type="number"
            placeholder="Rand per month"
            onChange={e => setMonthlyRand(Number(e.target.value))}
          />
        ) : (
          <input
            type="number"
            placeholder="kWh per month"
            onChange={e => setMonthlyKwhInput(Number(e.target.value))}
          />
        )}

        <p><b>{monthlyKwh.toFixed(1)} kWh / month</b></p>
      </div>

      {/* BATTERY % */}
      <div className="card">
        <h2>Battery Usage</h2>
        <div className="button-row">
          {[1, 0.9, 0.7, 0.5].map(p => (
            <button
              key={p}
              className={`btn btn-purple ${offGridPercent===p && "btn-neon"}`}
              onClick={() => setOffGridPercent(p)}
            >
              {p * 100}%
            </button>
          ))}
        </div>
      </div>

      {/* PHASE */}
      <div className="card">
        <h2>Phase</h2>
        <div className="button-row">
          {["single","three"].map(p => (
            <button
              key={p}
              className={`btn btn-purple ${phase===p && "btn-neon"}`}
              onClick={() => setPhase(p)}
            >
              {p === "single" ? "Three Phase" : "Single Phase"}
            </button>
          ))}
        </div>
      </div>
{/* ROOF INFORMATION */}
<div className="card">
  <h2>Roof Information</h2>

  <p><b>What type of roof?</b></p>
  <div className="button-row">
    {[
      "IBR Sink Roof",
      "Harvey Thatch Roof",
      "Corrugated Sink Roof",
      "Tile Roof",
    ].map(type => (
      <button
        key={type}
        className={`btn btn-purple ${roofType === type && "btn-neon"}`}
        onClick={() => setRoofType(type)}
      >
        {type}
      </button>
    ))}
  </div>

  <p style={{ marginTop: "15px" }}><b>Roof level</b></p>
  <div className="button-row">
    {["Flat Roof", "Raised Roof"].map(level => (
      <button
        key={level}
        className={`btn btn-purple ${roofLevel === level && "btn-neon"}`}
        onClick={() => setRoofLevel(level)}
      >
        {level}
      </button>
    ))}
  </div>
</div>

      {/* APPLIANCES */}
      <div className="card">
        <h2>Appliances</h2>
        {APPLIANCES.map(a => (
          <div key={a.name} className="row">
            <span>{a.name} ({a.kw} kW{a.auto ? " total" : ""})</span>
            <select onChange={e =>
              setQty({ ...qty, [a.name]: Number(e.target.value) })
            }>
              {[0,1,2,3,4].map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
        ))}
      </div>

      {/* SUMMARY */}
      <div className="card">
        <h2>System Summary</h2>

        <p>⚡ Connected load: <b>{connectedLoadKw.toFixed(1)} kW</b></p>
        <p>⚡ Realistic load (÷3): <b>{realisticLoadKw.toFixed(1)} kW</b></p>

        {phase === "three" && (
          <p>⚡ Per-phase load: <b>{perPhaseLoad.toFixed(1)} kW</b></p>
        )}

        <p>⚡ Inverter size: <b>{inverterKw} kW</b></p>
        <p>🔋 Battery required: <b>{batteryKwh.toFixed(1)} kWh</b></p>

        <p>☀️ PV (energy): <b>{pvEnergyKw.toFixed(1)} kWp</b></p>
        <p>☀️ PV total (energy + inverter): <b>{pvTotalKw.toFixed(1)} kWp</b></p>
      </div>

      {/* WHATSAPP BUTTON */}
      <div className="card">
        <button
          className={`btn ${whatsAppSent ? "btn-neon" : "btn-purple"}`}
          onClick={sendWhatsApp}
        >
          Send via WhatsApp
        </button>
      </div>

    </div>
  );
}
